import React from 'react';

import { ClientDetailsScreen } from 'types/state';
import { useServices } from 'hooks/useServices';
import { useServiceState } from 'hooks/useServiceState';
import { ClientDetailsContent } from 'containers/ClientDetails/ClientDetailsContent';
import { ClientDetailsProfileEditor } from 'containers/ClientDetails/ClientDetailsProfileEditor';
import { ClientDetailsLoginEditor } from 'containers/ClientDetails/ClientDetailsLoginEditor';
import { ClientDetailsPasswordEditor } from 'containers/ClientDetails/ClientDetailsPasswordEditor';
import { ClientDetailsSummary } from 'containers/ClientDetails/ClientDetailsSummary';
import { ClientDetailsPayments } from 'containers/ClientDetails/ClientDetailsPayments';
import { ClientDetailsProducts } from 'containers/ClientDetails/ClientDetailsProducts';
import { ClientDetailsPartnerBindingEditor } from 'containers/ClientDetails/ClientDetailsPartnerBindingEditor';
import { ClientDetailsProductEditor } from 'containers/ClientDetails/ClientDetailsProductEditor';
import { ClientDetailsRefundEditor } from 'containers/ClientDetails/ClientDetailsRefundEditor';
import { useAppDispatcher } from 'hooks/useAppDispatcher';
import { ModalLayerComponent } from 'design/modalLayer';
import { SidebarModalWindowComponent } from 'design/sidebarModal';

type TProps = {
  clientId: number;
  onClose(): void;
  onProfileEdited(): void;
  onLoginEdited(): void;
  onPasswordEdited(): void;
  onStatusEdited(): void;
};

export const ClientDetails = (props: TProps) => {
  const {
    clientId,
    onClose,
    onProfileEdited,
    onLoginEdited,
    onPasswordEdited,
    onStatusEdited,
  } = props;

  const dispatch = useAppDispatcher();

  const { getClientDetails, downloadBillReport } = useServices();

  const [detailsState, getDetailsByUserId] = useServiceState(getClientDetails);

  const getDetails = React.useCallback(() => {
    getDetailsByUserId({ clientId });
  }, [clientId, getDetailsByUserId]);

  const [activeScreen, setActiveScreen] = React.useState<ClientDetailsScreen>({
    name: 'Summary',
  });

  const handleEditProfile = React.useCallback(() => {
    setActiveScreen({
      name: 'ProfileEditor',
    });
  }, []);

  const handleProfileEditorCancel = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
  }, []);

  const handleEditLogin = React.useCallback(() => {
    setActiveScreen({
      name: 'LoginEditor',
    });
  }, []);

  const handleLoginEditorCancel = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
  }, []);

  const handleEditPassword = React.useCallback(() => {
    setActiveScreen({
      name: 'PasswordEditor',
    });
  }, []);

  const handlePasswordEditorCancel = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
  }, []);

  const handleRefundEditorCancel = React.useCallback(() => {
    setActiveScreen((prev) => {
      if (prev.name === 'RefundEditor' && prev.refundId !== null) {
        return {
          name: 'Payments',
        };
      }

      return {
        name: 'Summary',
      };
    });
  }, []);

  const handleRefundEditorSubmitSucceed = React.useCallback(() => {
    setActiveScreen({
      name: 'Payments',
    });

    getDetails();
  }, [getDetails]);

  const handleWrapperClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleArchiveActionDidSucceed = React.useCallback(() => {
    onStatusEdited();
  }, [onStatusEdited]);

  const handleProfileEditorSubmitDidSucceed = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
    getDetails();
    onProfileEdited();
  }, [getDetails, onProfileEdited]);

  const handlePasswordEditorSubmitDidSucceed = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
    onPasswordEdited();
  }, [onPasswordEdited]);

  const handleLoginEditorSubmitDidSucceed = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
    getDetails();
    onLoginEdited();
  }, [getDetails, onLoginEdited]);

  const handleContentTabClick = React.useCallback(
    (nextScreen: ClientDetailsScreen) => {
      setActiveScreen(nextScreen);
    },
    []
  );

  const handleEditPartnerBinding = React.useCallback(() => {
    setActiveScreen({
      name: 'PartnerBindingEditor',
    });
  }, []);

  const handlePartnerBindingDeleted = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
    getDetails();
  }, [getDetails]);

  const handlePartnerBindingEditorCancel = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
  }, []);

  const handlePartnerBindingEditorSubmitDidSucceed = React.useCallback(() => {
    setActiveScreen({
      name: 'Summary',
    });
    getDetails();
  }, [getDetails]);

  const handlePartnerProductEditorCancel = React.useCallback(() => {
    setActiveScreen({
      name: 'Products',
    });
  }, []);

  const handlePartnerProductEditorSubmitDidSucceed = React.useCallback(() => {
    setActiveScreen({
      name: 'Products',
    });
    getDetails();
  }, [getDetails]);

  const editProduct = React.useCallback((productId: number) => {
    setActiveScreen({ name: 'ProductEditor', productId });
  }, []);

  const editRefund = React.useCallback((refundId) => {
    setActiveScreen({ name: 'RefundEditor', refundId });
  }, []);

  const createRefund = React.useCallback(() => {
    setActiveScreen({ name: 'RefundEditor', refundId: null });
  }, []);

  const openBillReport = React.useCallback(
    (billId: number) => {
      downloadBillReport({
        billId,
        clientId,
      });
    },
    [clientId, downloadBillReport]
  );

  const manageCardPayment = React.useCallback(
    (paymentId) => {
      dispatch({
        name: 'ClientDetails__ManageCardPayment',
        paymentId,
      });
    },
    [dispatch]
  );

  const manageGiroPayment = React.useCallback(
    (paymentId) => {
      dispatch({
        name: 'ClientDetails__ManageGiroPayment',
        paymentId,
      });
    },
    [dispatch]
  );

  React.useEffect(() => {
    getDetails();
  }, [getDetails]);

  React.useLayoutEffect(() => {
    setActiveScreen({
      name: 'Summary',
    });
  }, [clientId]);

  const handleClose = React.useMemo(() => {
    switch (activeScreen.name) {
      case 'Summary':
      case 'Payments':
      case 'Products':
        return handleWrapperClose;
      default:
        return undefined;
    }
  }, [activeScreen, handleWrapperClose]);

  const clientProductToEdit = React.useMemo(() => {
    if (
      activeScreen.name === 'ProductEditor' &&
      detailsState &&
      detailsState.resolvedWith
    ) {
      const {
        resolvedWith: { clientProducts },
      } = detailsState;

      const { productId } = activeScreen;

      return clientProducts.find(
        (clientProduct) => clientProduct.id === productId
      );
    }
  }, [activeScreen, detailsState]);

  const teamProduct = React.useMemo(() => {
    return detailsState?.resolvedWith?.clientProducts.find((product) => {
      return product.m8ProductType === 'TEAM';
    });
  }, [detailsState?.resolvedWith?.clientProducts]);

  return (
    <ModalLayerComponent theme="transparent">
      <SidebarModalWindowComponent
        onMisclick={handleClose}
        onEscape={handleClose}
        onClose={handleClose}
      >
        {detailsState &&
          detailsState.resolvedWith &&
          (activeScreen.name === 'Summary' ||
            activeScreen.name === 'Payments' ||
            activeScreen.name === 'Products') && (
            <ClientDetailsContent
              {...detailsState.resolvedWith}
              activeScreen={activeScreen}
              onTabClick={handleContentTabClick}
            >
              {activeScreen.name === 'Summary' && (
                <ClientDetailsSummary
                  {...detailsState.resolvedWith}
                  onArchiveActionDidSucceed={handleArchiveActionDidSucceed}
                  onEditLogin={handleEditLogin}
                  onEditPassword={handleEditPassword}
                  onEditProfile={handleEditProfile}
                  onEditPartnerBinding={handleEditPartnerBinding}
                  onPartnerBindingDeleted={handlePartnerBindingDeleted}
                  onCreateRefund={createRefund}
                />
              )}
              {activeScreen.name === 'Payments' && (
                <ClientDetailsPayments
                  {...detailsState.resolvedWith}
                  onEditRefund={editRefund}
                  onManageBill={openBillReport}
                  onManageCardPayment={manageCardPayment}
                  onManageGiroPayment={manageGiroPayment}
                />
              )}
              {activeScreen.name === 'Products' && (
                <ClientDetailsProducts
                  onEditProduct={editProduct}
                  {...detailsState.resolvedWith}
                />
              )}
            </ClientDetailsContent>
          )}
        {detailsState &&
          detailsState.resolvedWith &&
          activeScreen.name === 'ProfileEditor' && (
            <ClientDetailsProfileEditor
              clientId={clientId}
              userProfileInfo={detailsState.resolvedWith.userProfileInfo}
              onCancel={handleProfileEditorCancel}
              onSubmitDidSucceed={handleProfileEditorSubmitDidSucceed}
            />
          )}
        {detailsState &&
          detailsState.resolvedWith &&
          activeScreen.name === 'LoginEditor' && (
            <ClientDetailsLoginEditor
              clientId={clientId}
              values={detailsState.resolvedWith.clientInfo}
              onCancel={handleLoginEditorCancel}
              onSubmitDidSucceed={handleLoginEditorSubmitDidSucceed}
            />
          )}
        {detailsState &&
          detailsState.resolvedWith &&
          activeScreen.name === 'PasswordEditor' && (
            <ClientDetailsPasswordEditor
              clientId={clientId}
              onCancel={handlePasswordEditorCancel}
              onSubmitDidSucceed={handlePasswordEditorSubmitDidSucceed}
            />
          )}
        {detailsState &&
          detailsState.resolvedWith &&
          activeScreen.name === 'RefundEditor' && (
            <ClientDetailsRefundEditor
              {...detailsState.resolvedWith}
              clientId={clientId}
              refundId={activeScreen.refundId}
              onCancel={handleRefundEditorCancel}
              onSubmitDidSucceed={handleRefundEditorSubmitSucceed}
            />
          )}
        {detailsState &&
          detailsState.resolvedWith &&
          activeScreen.name === 'PartnerBindingEditor' &&
          teamProduct && (
            <ClientDetailsPartnerBindingEditor
              {...detailsState.resolvedWith}
              clientProduct={teamProduct}
              clientId={clientId}
              onCancel={handlePartnerBindingEditorCancel}
              onSubmitDidSucceed={handlePartnerBindingEditorSubmitDidSucceed}
            />
          )}
        {clientProductToEdit && activeScreen.name === 'ProductEditor' && (
          <ClientDetailsProductEditor
            clientId={clientId}
            partnerId={detailsState?.resolvedWith?.partner?.id}
            initialCalculation={clientProductToEdit.calculation}
            m8ProductType={clientProductToEdit.m8ProductType}
            onCancel={handlePartnerProductEditorCancel}
            onSubmitDidSucceed={handlePartnerProductEditorSubmitDidSucceed}
          />
        )}
      </SidebarModalWindowComponent>
    </ModalLayerComponent>
  );
};
